import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MdFilterList } from "react-icons/md";
import { formatDateShort } from "../utils/dateFormatter";
import { Helmet } from "react-helmet";

export default function HomePage({ movies, onVote, votePoints, updateVotePoints, user, tab, setTab, languageFilter, setLanguageFilter, loadingMovies, hasMore, setPage, fetchMovies, page }) {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef(null);

  // Wrapper function to pass points directly to onVote
  const voteWithPoints = (movieId, points) => {
    onVote(movieId, points);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);
  // compute UTC day start and cutoff (one month ago)
  const now = new Date();
  const todayStartUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const cutoffDate = new Date(todayStartUTC);
  cutoffDate.setUTCMonth(cutoffDate.getUTCMonth() - 1);
  const cutoffStr = cutoffDate.toISOString().split("T")[0];

  // defensive: ensure movies is an array
  const received = Array.isArray(movies) ? movies : [];

  // derive filtered list client-side (works even if server filtering is off)
  const filteredMovies = received.filter(m => {
    if (!m || !m.releaseDate) return false;
    const releaseDateStr = new Date(m.releaseDate).toISOString().split("T")[0];
    if (tab === 'old') return releaseDateStr < cutoffStr;
    // upcoming: include releases from the last month and future
    return releaseDateStr >= cutoffStr;
  });

  // sort: for upcoming/current tab we want recent past (within last month) first (newest -> oldest),
  // then future (earliest -> latest). For 'old' tab just sort desc by releaseDate.
  const sortedMovies = (() => {
    if (tab === 'old') {
      return filteredMovies.slice().sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
    }
    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const past = [];
    const future = [];
    filteredMovies.forEach(m => {
      const rd = new Date(m.releaseDate);
      if (rd < todayStart) past.push(m);
      else future.push(m);
    });
    past.sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate)); // newest past first
    future.sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate)); // earliest future first
    return [...past, ...future];
  })();

  // Separate current and upcoming movies when on upcoming tab
  const currentMovies = tab === 'upcoming' ? sortedMovies.filter(m => {
    const rd = new Date(m.releaseDate);
    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    return rd < todayStart;
  }) : [];
  
  const upcomingMovies = tab === 'upcoming' ? sortedMovies.filter(m => {
    const rd = new Date(m.releaseDate);
    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    return rd >= todayStart;
  }) : sortedMovies;

  return (
    <div className="homepage-container">
      <Helmet>
        <title>Tollywood Movies – Hype Rankings, Ratings & Votes | TollyHype</title>
        <meta name="description" content="list of Tollywood movies upcoming and currently released in theatres." />
        <link rel="canonical" href="https://tollyhype.com/movies" />
      </Helmet>

      <div className="section-header">
        <h1>{tab === 'old' ? '🎬 Released Classics' : '🌟 Movies'}</h1>
        <div className="tab-switcher">
          {/* Filter Icon - Only show for upcoming tab */}
          {tab === 'upcoming' && (
            <div className="filter-icon-wrapper" ref={filterRef}>
              <button
                className={`filter-icon-btn ${languageFilter !== null ? 'active' : ''}`}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                title="Filter by language"
              >
                <MdFilterList size={20} />
                {languageFilter !== null && <span className="filter-badge"></span>}
              </button>
              
              {showFilterDropdown && (
                <div className="filter-dropdown">
                  <div className="filter-dropdown-title">Filter by Language</div>
                  <button
                    className={`filter-dropdown-option ${languageFilter === null ? 'active' : ''}`}
                    onClick={() => {
                      setLanguageFilter(null);
                      setShowFilterDropdown(false);
                    }}
                  >
                    All Languages
                    {languageFilter === null && <span className="checkmark">✓</span>}
                  </button>
                  <button
                    className={`filter-dropdown-option ${languageFilter === 'english' ? 'active' : ''}`}
                    onClick={() => {
                      setLanguageFilter('english');
                      setShowFilterDropdown(false);
                    }}
                  >
                    English
                    {languageFilter === 'english' && <span className="checkmark">✓</span>}
                  </button>
                  <button
                    className={`filter-dropdown-option ${languageFilter === 'telugu' ? 'active' : ''}`}
                    onClick={() => {
                      setLanguageFilter('telugu');
                      setShowFilterDropdown(false);
                    }}
                  >
                    Telugu
                    {languageFilter === 'telugu' && <span className="checkmark">✓</span>}
                  </button>
                  <button
                    className={`filter-dropdown-option ${languageFilter === 'tamil' ? 'active' : ''}`}
                    onClick={() => {
                      setLanguageFilter('tamil');
                      setShowFilterDropdown(false);
                    }}
                  >
                    Tamil
                    {languageFilter === 'tamil' && <span className="checkmark">✓</span>}
                  </button>
                </div>
              )}
            </div>
          )}
          
          <button 
            className={`tab-btn ${tab === 'upcoming' ? 'active' : ''}`} 
            onClick={() => {
              setTab('upcoming');
              setLanguageFilter('telugu'); // Set Telugu as default when switching to upcoming tab
            }}
          >
            Upcoming & Current
          </button>
          <button 
            className={`tab-btn ${tab === 'old' ? 'active' : ''}`} 
            onClick={() => {
              setTab('old');
              setLanguageFilter(null); // Reset filter when switching tabs
            }}
          >
            Released
          </button>
        </div>
      </div>

      {tab === 'upcoming' && currentMovies.length > 0 && (
        <div className="section">
          <div className="section-title">
            <h2>🔥 Currently Trending</h2>
            <p className="section-subtitle">Released in the last month</p>
          </div>
          <div className="movie-grid">
            {currentMovies.map(m => (
              <MovieCard key={m.id} m={m} votePoints={votePoints} updateVotePoints={updateVotePoints} onVote={onVote} user={user} cutoffStr={cutoffStr} voteWithPoints={voteWithPoints} />
            ))}
          </div>
        </div>
      )}

      {/* Visual placeholder between current and upcoming sections */}
      {/* {tab === 'upcoming' && currentMovies.length > 0 && upcomingMovies.length > 0 && (
        <div className="section" style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
          <div style={{ width: '100%', maxWidth: '920px', textAlign: 'center' }}>
            <div className="ad-placeholder" style={{ height: '90px' }}>Ad placeholder — responsive</div>
          </div>
        </div>
      )} */}

      {tab === 'upcoming' && upcomingMovies.length > 0 && (
        <div className="section">
          <div className="section-title">
            <h2>🚀 Coming Soon</h2>
            <p className="section-subtitle">Upcoming releases</p>
          </div>
          <div className="movie-grid">
            {upcomingMovies.map(m => (
              <MovieCard key={m.id} m={m} votePoints={votePoints} updateVotePoints={updateVotePoints} onVote={onVote} user={user} cutoffStr={cutoffStr} voteWithPoints={voteWithPoints} />
            ))}
          </div>
        </div>
      )}

      {tab === 'old' && sortedMovies.length > 0 && (
        <div className="section">
          <div className="movie-grid">
            {sortedMovies.map(m => (
              <MovieCard key={m.id} m={m} votePoints={votePoints} updateVotePoints={updateVotePoints} onVote={onVote} user={user} cutoffStr={cutoffStr} voteWithPoints={voteWithPoints} />
            ))}
          </div>
        </div>
      )}

      {tab === 'old' && sortedMovies.length === 0 && (
        <div className="section">
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <p>No released movies found</p>
          </div>
        </div>
      )}

      {tab === 'upcoming' && sortedMovies.length === 0 && (
        <div className="section">
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <p>No upcoming or current movies found</p>
          </div>
        </div>
      )}

      {loadingMovies && (
        <div className="load-more-section">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading movies...</p>
          </div>
        </div>
      )}

      {!loadingMovies && !hasMore && sortedMovies.length > 0 && (
        <div className="load-more-section">
          <div className="end-indicator">
            <p>✓ All movies loaded</p>
          </div>
        </div>
      )}

      {!loadingMovies && hasMore && sortedMovies.length > 0 && (
        <div className="load-more-container">
          <button className="load-more-btn" onClick={() => {
            setPage(p => {
              const next = p + 1;
              fetchMovies(next, tab, true, languageFilter);
              return next;
            });
          }}>
            <span className="arrow">↓</span>
            <span>Load More</span>
          </button>
        </div>
      )}

      <VideoModal />
    </div>
  );
}

// Extracted MovieCard component for cleaner code
function MovieCard({ m, votePoints, updateVotePoints, onVote, user, cutoffStr, voteWithPoints }) {
  const navigate = useNavigate();
  const [showSlider, setShowSlider] = useState(false);
  const [sliderValue, setSliderValue] = useState(Number(votePoints[m.id] || 1));

  const maxPoints = user ? (user.pointsLeftToday || 0) : 10;
  const releaseDateStr = m.releaseDate ? new Date(m.releaseDate).toISOString().split("T")[0] : null;
  const cutoffDate = new Date();
  cutoffDate.setUTCMonth(cutoffDate.getUTCMonth() - 1);
  const cutoffStr_local = cutoffDate.toISOString().split("T")[0];
  const isOld = releaseDateStr && releaseDateStr < cutoffStr_local;

  const handleHypeClick = (e) => {
    e.stopPropagation();
    if (!user || isOld) return;
    // Initialize slider value to 1 if not set, or use current value
    const currentValue = Number(votePoints[m.id] || 1);
    setSliderValue(currentValue);
    updateVotePoints(m.id, currentValue.toString());
    setShowSlider(true);
  };

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setSliderValue(value);
    // Update votePoints immediately so onVote uses the correct value
    updateVotePoints(m.id, value.toString());
  };

  const handleVote = (e) => {
    if (e) e.stopPropagation();
    if (sliderValue === 0) {
      setShowSlider(false);
      return;
    }
    setShowSlider(false);
    // Update votePoints for UI consistency
    updateVotePoints(m.id, sliderValue.toString());
    // Pass slider value directly to avoid state timing issues
    if (voteWithPoints) {
      voteWithPoints(m.id, sliderValue);
    } else {
      onVote(m.id);
    }
  };

  useEffect(() => {
    const currentValue = Number(votePoints[m.id] || 0);
    setSliderValue(currentValue);
    // Initialize votePoints if not set
    if (!votePoints[m.id] && currentValue === 0) {
      updateVotePoints(m.id, "0");
    }
  }, [votePoints, m.id, updateVotePoints]);

  // Close slider when clicking outside
  useEffect(() => {
    if (!showSlider) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.simple-slider-container') && !e.target.closest('.hype-btn')) {
        setShowSlider(false);
        setSliderValue(Number(votePoints[m.id] || 1));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSlider, votePoints, m.id]);

  return (
    <div 
      className="movie-card"
      onClick={() => navigate(`/movie/${m.slug || m.id}`)}
      style={{ cursor: "pointer" }}
    >
      <div className="poster-wrap" tabIndex={0} aria-label={`${m.title} poster`}>
        {m.posterUrl ? (
          <img 
            className="poster" 
            src={m.posterUrl} 
            alt={m.title} 
            loading="lazy"
            width="300"
            height="450"
            decoding="async"
            fetchPriority="low"
          />
        ) : (
          <div className="poster poster-placeholder" style={{ width: '300px', height: '450px' }}>No image</div>
        )}
        {m.youtubeId && (
          <button 
            className="poster-overlay" 
            onClick={(e) => {
              e.stopPropagation();
              openVideo(m.youtubeId);
            }} 
            aria-label={`Play trailer for ${m.title}`}
          >
            <span className="play-icon">▶</span>
          </button>
        )}
      </div>
      <div className="movie-info">
        <div className="movie-title">{m.title}</div>
        <div className="movie-meta">
          <div className="movie-points">
            <span className="points-icon">⭐</span>
            {m.totalPoints} points
          </div>
          <div className="movie-date">{formatDateShort(m.releaseDate)}</div>
        </div>
        <div className="vote-row">
          {showSlider && user && !isOld ? (
            <>
              <div className="simple-slider-container" onClick={(e) => e.stopPropagation()}>
                <div className="slider-labels">
                  <span className="slider-min">0</span>
                  <span className="slider-current">{sliderValue}</span>
                  <span className="slider-max">{maxPoints}</span>
                </div>
                <div className="slider-wrapper-inline">
                  <input
                    type="range"
                    min="0"
                    max={maxPoints}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    className="points-slider-inline"
                  />
                  <div 
                    className="slider-progress-fill-inline"
                    style={{
                      width: `${maxPoints > 0 ? (sliderValue / maxPoints) * 100 : 0}%`
                    }}
                  ></div>
                </div>
              </div>
              <div
                className="btn-wrapper"
                data-tooltip={
                  isOld ? 'Voting disabled for released movies' : ''
                }
              >
                <button
                  className="hype-btn"
                  onClick={handleVote}
                  disabled={sliderValue === 0}
                  aria-disabled={sliderValue === 0}
                >
                  Hype
                </button>
              </div>
            </>
          ) : (
            <>
              <div
                className="btn-wrapper"
                data-tooltip={
                  isOld ? 'Voting disabled for released movies' : ''
                }
              >
                <button
                  className="hype-btn"
                  onClick={handleHypeClick}
                  disabled={!user || isOld}
                  aria-disabled={!user || isOld}
                >
                  {isOld ? 'Closed' : 'Hype'}
                </button>
              </div>
              {!user && <div className="login-prompt">Login to vote</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Video modal component (keeps local state inside file)
function VideoModal() {
  // state is stored on window to be accessible from poster buttons without prop drilling
  // this is a small pragmatic approach for this page; we could lift state if needed
  const [openId, setOpenId] = useState(window.__movieModalId || null);

  useEffect(() => {
    // expose open/close helpers
    window.openMovieModal = (youtubeId) => {
      window.__movieModalId = youtubeId;
      setOpenId(youtubeId);
    };
    window.closeMovieModal = () => {
      window.__movieModalId = null;
      setOpenId(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') window.closeMovieModal(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.openMovieModal = undefined;
      window.closeMovieModal = undefined;
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!openId) return null;

  return (
    <div className="video-modal" role="dialog" aria-modal="true">
      <div className="video-modal-backdrop" onClick={() => window.closeMovieModal()} />
      <div className="video-modal-body">
        <button className="video-modal-close" onClick={() => window.closeMovieModal()} aria-label="Close">✕</button>
        <div className="video-embed">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${openId}?autoplay=1`}
            title="Movie Trailer"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}

// helper used in poster onclick
function openVideo(youtubeId) {
  if (window.openMovieModal) window.openMovieModal(youtubeId);
}
